import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import GetUser from "../utils/getUser";
import UseGraph from "../utils/useGraph";
import formQueryUrl from "../utils/formQueryUrl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowsRotate,
  faBan,
  faCircleCheck,
  faEnvelopeOpenText,
  faMoneyBillWave,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

type WidgetConfig = {
  status: string;
  label: string;
  icon: IconDefinition;
  iconClass: string;
  tileClass: string;
};

const widgetData: WidgetConfig[] = [
  {
    status: "Inviting",
    label: "Inviting",
    icon: faEnvelopeOpenText,
    iconClass: "text-pink-600 dark:text-pink-300",
    tileClass:
      "bg-gradient-to-br from-pink-500/15 to-pink-500/5 border-pink-400/30 dark:border-pink-400/25",
  },
  {
    status: "Auto-Renewals",
    label: "Auto Renewals",
    icon: faArrowsRotate,
    iconClass: "text-indigo-600 dark:text-indigo-300",
    tileClass:
      "bg-gradient-to-br from-indigo-500/15 to-indigo-500/5 border-indigo-400/30 dark:border-indigo-400/25",
  },
  {
    status: "Payment-Failed",
    label: "Payment Failed",
    icon: faTriangleExclamation,
    iconClass: "text-amber-600 dark:text-amber-300",
    tileClass:
      "bg-gradient-to-br from-amber-500/15 to-amber-500/5 border-amber-400/30 dark:border-amber-400/25",
  },
  {
    status: "Payment-Received",
    label: "Payment Received",
    icon: faMoneyBillWave,
    iconClass: "text-violet-600 dark:text-violet-300",
    tileClass:
      "bg-gradient-to-br from-violet-500/15 to-violet-500/5 border-violet-400/30 dark:border-violet-400/25",
  },
  {
    status: "Renewed",
    label: "Renewed",
    icon: faCircleCheck,
    iconClass: "text-emerald-600 dark:text-emerald-300",
    tileClass:
      "bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border-emerald-400/30 dark:border-emerald-400/25",
  },
  {
    status: "Lapsed",
    label: "Lapsed",
    icon: faBan,
    iconClass: "text-slate-600 dark:text-slate-300",
    tileClass:
      "bg-gradient-to-br from-slate-500/15 to-slate-500/5 border-slate-400/30 dark:border-slate-400/25",
  },
];

export default function TopBarWidget() {
  const user = GetUser();
  const [loading, setLoading] = useState<boolean>(true);
  const [widgetCount, setWidgetCount] = useState<Record<string, any>[]>([]);
  const isMyCustomer = window.location.pathname === "/my-customers";
  const { startDate, endDate } = UseGraph();

  useEffect(() => {
    const findWidgets = async () => {
      setLoading(true);
      const filtersArray = [
        isMyCustomer && { myCustomer: user?.code },
        startDate !== null && { startDate },
        endDate !== null && { endDate },
      ];
      try {
        const res = await axiosInstance.get(
          `/stats/chase/counts${formQueryUrl(filtersArray, "?")}`
        );
        setWidgetCount(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    findWidgets();
  }, [user?.code, isMyCustomer, startDate, endDate]);

  // Three across: the six statuses fall as two even rows, not 4 + a gap.
  return (
    <div className="mt-6 grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 animate-fade-in">
      {widgetData.map((item) => {
        const count =
          Number(
            widgetCount.find((widgetC) => widgetC.status === item.status)?.count
          ) || 0;

        if (loading) {
          return (
            <div
              className="animate-pulse w-full h-[88px] bg-surface-elevated rounded-xl border border-edge-faint"
              key={item.status}
            ></div>
          );
        }

        return (
          <div
            className="bg-surface w-full rounded-xl border border-edge shadow-card hover:-translate-y-1 hover:border-indigo-400/30 hover:shadow-glow-sm transition-all duration-300"
            key={item.status}
          >
            <div className="flex items-center p-4 gap-3 font-medium">
              <div
                className={`flex items-center justify-center w-11 h-11 rounded-xl border shrink-0 ${item.tileClass}`}
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  className={`text-[18px] ${item.iconClass}`}
                />
              </div>
              <div className="flex flex-col min-w-0">
                <p className="font-medium leading-5 text-[13px] xl:text-[14px] text-light-gray truncate !capitalize">
                  {item.label}
                </p>
                <p className="text-text-primary font-bold text-[22px] leading-tight">
                  {count}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
